import React, { useRef, useState } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Title,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useTheme } from "../../../ThemeContext";
import zoomPlugin from "chartjs-plugin-zoom";
import annotationPlugin from "chartjs-plugin-annotation";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  zoomPlugin,
  annotationPlugin
);

const AnalyticsChart = ({
  structure,
  data,
  sections,
  StartDate,
  EndDate,
  selectedDay,
}) => {
  const { theme } = useTheme();
  const chartRef = useRef(null); // To access chart instance
  const [structureColors, setStructureColors] = useState({});

  const getStructureColor = (structureName) => {
    const storedColors = JSON.parse(localStorage.getItem("structureColors")) || {};
    if (!storedColors[structureName]) {
      const newColor = getRandomColor();
      storedColors[structureName] = newColor;
      localStorage.setItem("structureColors", JSON.stringify(storedColors));
      setStructureColors(storedColors);
      return newColor;
    }
    return storedColors[structureName];
  };

  const getRandomColor = () => {
    const letters = "0123456789ABCDEF";
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
  };

  const processChartData = () => {
    if (!data || data.length === 0) {
      return { labels: [], datasets: [] };
    }

    const structureQuantitiesByMonth = {};
    const labels = [];

    data.forEach((item) => {
      const isStructureSelected =
        structure.length === 0 || structure.includes(item.Structure);
      const isDateInRange =
        (!StartDate || item.Date >= StartDate) &&
        (!EndDate || item.Date <= EndDate);

      if (isStructureSelected && isDateInRange) {
        const quantity = parseInt(item.Quantity, 10) || 0;
        const date = new Date(item.Date);
        const month = date.toLocaleString("default", { month: "short" });
        const year = date.getFullYear();
        const day = date.getDate();

        if (!selectedDay || selectedDay === day) {
          const monthYear = `${month} ${year}`;

          if (!structureQuantitiesByMonth[monthYear]) {
            structureQuantitiesByMonth[monthYear] = {};
            labels.push(monthYear);
          }

          if (!structureQuantitiesByMonth[monthYear][item.Structure]) {
            structureQuantitiesByMonth[monthYear][item.Structure] = 0;
          }

          structureQuantitiesByMonth[monthYear][item.Structure] += quantity;
        }
      }
    });

    const datasets = structure.map((structureName) => {
      const dataForStructure = labels.map((label) => {
        return structureQuantitiesByMonth[label] &&
          structureQuantitiesByMonth[label][structureName]
          ? structureQuantitiesByMonth[label][structureName]
          : 0;
      });

      return {
        label: structureName,
        data: dataForStructure,
        backgroundColor: getStructureColor(structureName),
        borderColor: getStructureColor(structureName),
        borderWidth: 1,
      };
    });

    return { labels, datasets };
  };

  const chartData = processChartData();
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // Hides legend from top
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            const { datasetIndex, dataIndex } = tooltipItems[0];
            const { label } = chartData.datasets[datasetIndex];
            const date = chartData.labels[dataIndex];
            const quantity = tooltipItems[0].raw;
            return `${label} on ${date}: ${quantity} Nos`;
          },
        },
        backgroundColor: theme === "dark" ? "#333" : "#fff",
        titleColor: theme === "dark" ? "#fff" : "#000",
        bodyColor: theme === "dark" ? "#fff" : "#000",
      },
      zoom: {
        pan: {
          enabled: true,
          mode: "xy",
        },
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "xy",
        },
      },
      annotation: {
        annotations: [
          {
            type: "line",
            yMin: 20,
            yMax: 20,
            borderColor: "red",
            borderWidth: 2,
            label: {
              content: "Threshold",
              enabled: true,
              backgroundColor: "rgba(255,99,132,0.2)",
            },
          },
        ],
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme === "dark" ? "#fff" : "#000",
        },
        grid: {
          color: theme === "dark" ? "#555" : "#e5e5e5",
        },
      },
      y: {
        ticks: {
          color: theme === "dark" ? "#fff" : "#000",
        },
        grid: {
          color: theme === "dark" ? "#555" : "#e5e5e5",
        },
      },
    },
  };

  const downloadAsPNG = () => {
    const chartInstance = chartRef.current;
    if (chartInstance) {
      const link = document.createElement("a");
      link.download = "chart.png";
      link.href = chartInstance.toBase64Image();
      link.click();
    }
  };

  const downloadAsCSV = () => {
    const rows = [["Date", ...structure]];
    chartData.labels.forEach((label, index) => {
      const row = [label];
      chartData.datasets.forEach((dataset) => {
        row.push(dataset.data[index]);
      });
      rows.push(row);
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      rows.map((row) => row.join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "chart.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className={`shadow-md rounded-lg p-4 ${
        theme === "dark" ? "bg-gray-800 text-white" : "bg-white text-black"
      }`}
      style={{ width: "100%", height: "500px" }}
    >
      <div style={{ height: "400px" }}>
        <Bar ref={chartRef} data={chartData} options={chartOptions} />
      </div>
      <div className="flex gap-4 mt-4">
        <button
          onClick={downloadAsPNG}
          className={`px-4 py-2 rounded ${
            theme === "dark" ? "bg-gray-700 text-white" : "bg-blue-500 text-white"
          }`}
        >
          Download PNG
        </button>
        <button
          onClick={downloadAsCSV}
          className={`px-4 py-2 rounded ${
            theme === "dark" ? "bg-gray-700 text-white" : "bg-green-500 text-white"
          }`}
        >
          Download CSV
        </button>
      </div>
    </div>
  );
};

export default AnalyticsChart;
